import React, { useState } from "react";
import { useParams } from "react-router";
import { Heading, Loader, Popup, Tab, Tabs } from "uikit/components";
import { BackLink } from "components";
import styles from "./RegionEventsPage.module.styl";
import { useRegionEvents } from "hooks/events";
import { useSportTypes } from "hooks/dictionaries";
import RegionEventsView from "components/regionSummary/RegionEventsView/RegionEventsView";
import Pagination from "uikit/components/Pagination/Pagination";
import math from "lodash";
import { useContextPerson } from "hooks/persons";
import { Flags } from "utils";
import { Helmet } from "react-helmet-async";
import FiltersPanel from "components/dashboard/Filters/FiltersPanel";

export default function RegionEventsPage() {
    const pageSize = 10;

    const [payload, setPayload] = useState({
        pageNumber: 1,
        pageSize: pageSize,
        federationEventTypeId: [],
    });

    const AllSportTypesOption = {
        label: "Все виды спорта",
        value: {
            name: "Все виды спорта",
            id: null,
        },
    };

    const [sportTypeState, setSportTypeState] = useState(null);
    const [eventTypeState, setEventTypeState] = useState(null);

    const { regionId } = useParams();
    const { flags, isLoading: isContextLoading } = useContextPerson();
    const { isLoading, data } = useRegionEvents(regionId, payload, { keepPreviousData: true });
    const { isLoading: isSportTypesLoading, data: sportTypes } = useSportTypes();

    const federationEventTypes = {
        All: null,
        SportEvent: 1,
        TrainingEvent: 2,
        PhysicalEvent: 3,
        Other: 4,
    };

    const eventTypes = [
        { type: federationEventTypes.All, label: "Все мероприятия", dataTestName: "all-events" },
        {
            type: federationEventTypes.PhysicalEvent,
            label: "Физкультурные мероприятия",
            dataTestName: "physical-events",
        },
        {
            type: federationEventTypes.SportEvent,
            label: "Спортивные мероприятия",
            dataTestName: "sport-events",
        },
        {
            type: federationEventTypes.TrainingEvent,
            label: "Тренировочные мероприятия",
            dataTestName: "training-events",
        },
    ];

    const handlePageNumberChange = (newPageNumber) => {
        setPayload((payload) => ({ ...payload, pageNumber: newPageNumber, pageSize: pageSize }));
    };

    const handleEventTypeChange = (eventTypeId) => {
        setPayload((payload) => ({
            ...payload,
            federationEventTypeId: mapEvents(eventTypeId),
            sportTypeId: null,
            pageNumber: 1,
        }));
        setSportTypeState(null);
        setEventTypeState(eventTypeId);
    };

    const handleSportTypeChange = (sportType) => {
        setPayload((payload) => ({
            ...payload,
            federationEventTypeId: mapEvents(eventTypeState),
            sportTypeId: sportType.id,
            pageNumber: 1,
        }));
        setSportTypeState(sportType);
    };

    function mapEvents(eventTypeId) {
        if (eventTypeId == null) {
            return [];
        } else if (eventTypeId === federationEventTypes.PhysicalEvent) {
            return [federationEventTypes.PhysicalEvent, federationEventTypes.Other];
        } else {
            return [eventTypeId];
        }
    }

    if (isLoading || isContextLoading || isSportTypesLoading) {
        return (
            <Popup blockClickOnOverlay appearance="adaptive">
                <Loader
                    position={"margin-auto"}
                    text={"Подождите. Данные загружаются."}
                    testName="loader-text"
                />
            </Popup>
        );
    }

    if (data && sportTypes) {
        let sportTypeOptions = sportTypes.sportTypes.map((sportType) => ({
            label: sportType.name,
            value: {
                id: sportType.id,
                name: sportType.name,
            },
        }));
        sportTypeOptions.unshift(AllSportTypesOption);
        let filters = [
            {
                header: "Вид спорта",
                onValueChanged: (data) => handleSportTypeChange(data),
                options: sportTypeOptions,
                value: sportTypeState
                    ? { label: sportTypeState.name, value: sportTypeState }
                    : AllSportTypesOption,
            },
        ];
        return (
            <>
                <Helmet>
                    <meta
                        name="apple-mobile-web-app-title"
                        content={`Календарный план физкультурных и спортивных мероприятий: ${data.regionName}`}
                    />
                    <meta
                        name="application-name"
                        content={`Календарный план физкультурных и спортивных мероприятий: ${data.regionName}`}
                    />
                </Helmet>
                {!flags?.[Flags.HideDashboardBackButton] && (
                    <BackLink
                        to={`/region-summary/${regionId}`}
                        text={"Назад"}
                        name={"region_federation_events_back_link"}
                    />
                )}
                <Heading size={"h1"} className={styles.heading} name="region-events-main-header">
                    Календарный план физкультурных и спортивных мероприятий:
                </Heading>
                <Heading size={"h1"} data-test-name="region-events-header">
                    {data.regionName}, {data.reportYear} год
                </Heading>
                <div className={styles.eventTypesContainer}>
                    <Tabs>
                        {eventTypes.map((eventType) => (
                            <Tab
                                key={eventType.type}
                                style={styles.eventTypeTab}
                                isActive={payload.federationEventTypeId?.includes(eventType.type)}
                                onClick={() => handleEventTypeChange(eventType.type)}
                                dataTestName={eventType.dataTestName}
                            >
                                {eventType.label}
                            </Tab>
                        ))}
                    </Tabs>
                </div>
                <FiltersPanel filters={filters} testName={"filter-panel"} />
                <div className={styles.overallContainer}>
                    <div className={styles.subHeader}>
                        Всего мероприятий:
                        <div className={styles.subHeaderValue}>
                            {data.eventsOverallData.eventsCount}
                        </div>
                    </div>
                    <div className={styles.subHeader}>
                        Завершенных мероприятий:
                        <div className={styles.subHeaderValue}>
                            {data.eventsOverallData.completedEventsCount}
                        </div>
                    </div>
                    <div className={styles.subHeader}>
                        Мероприятий с результатами:
                        <div className={styles.subHeaderValue}>
                            {data.eventsOverallData.eventsWithResultsCount}
                        </div>
                    </div>
                    <div className={styles.subHeader}>
                        Всего участников:
                        <div className={styles.subHeaderValue}>
                            {data.eventsOverallData.participantsCount}
                        </div>
                    </div>
                </div>
                <RegionEventsView eventsList={data.eventsList} />
                {data.eventsOverallData.eventsCount > pageSize && (
                    <Pagination
                        pageCount={math.ceil(data.eventsOverallData.eventsCount / pageSize)}
                        forcePage={payload.pageNumber}
                        onPageChange={handlePageNumberChange}
                        className={styles.pagination}
                    />
                )}
            </>
        );
    }
}
